// The estimated price, and the one rule about when it exists.
//
// Lives here rather than in the panel that draws it because two things need to
// agree: the panel shows it only once every field is answered, and the saved
// record of the call must mean the same thing by it. A price stored for a call
// that was still missing half its answers would be a number nobody could trust
// later.

import type { FieldState } from './extraction';
import { isPricingConfig, priceFor } from '../pricing/scope';

/**
 * Stubbed. Not a real estimate and not derived from anything - it stands in
 * until pricing exists, and is deliberately a constant so nobody mistakes it
 * for a calculation.
 */
export const PLACEHOLDER_ESTIMATE_USD = 10732;

export const PLACEHOLDER_ESTIMATE_CENTS = PLACEHOLDER_ESTIMATE_USD * 100;

/** Every field has an answer, so the checklist is done. */
export function isComplete(fields: FieldState[]): boolean {
  return fields.length > 0 && fields.every((field) => field.status === 'filled');
}

/**
 * What to record as the price of a call.
 *
 * For the pricing scope this is the model's own number, and it does NOT wait
 * for a complete checklist - the model scores a partial scope by design, and
 * refusing to record one would throw away the estimate the inspector was
 * actually looking at.
 *
 * For an ordinary checklist there is nothing to compute from, so the old
 * placeholder rule stands: a stub, only once every field is answered.
 */
export function estimateCents(
  fields: FieldState[],
  config?: { id: string; builtinKey?: string | null } | null,
): number | null {
  if (isPricingConfig(config)) {
    const answers = Object.fromEntries(fields.map((f) => [f.id, f.value]));
    const priced = priceFor(answers);
    return priced ? Math.round(priced.mid * 100) : null;
  }
  return isComplete(fields) ? PLACEHOLDER_ESTIMATE_CENTS : null;
}
